// lib/qqq/synthesis.js
// ═══════════════════════════════════════════════════════════
// QQQ SESSION BRIEF — verdict + plan synthesis
// ═══════════════════════════════════════════════════════════
// Deterministic 8-signal scoring + rules-based Execution Plan.
//
// Signals (all derived from sources.js _raw data):
//   1. Above PDH            — price vs prior-day high
//   2. Gamma regime         — price vs gamma flip
//   3. VIX contango         — 9D < 30D (fear resolving)
//   4. PCR bullish          — put/call < 0.85
//   5. Tech leading         — XLK change > SPY change
//   6. Dollar weak          — DXY down > 0.1% (skipped in V1 — no DXY source yet)
//   7. Yield curve healthy  — 10Y > 2Y (not inverted)
//   8. Clean window         — no high-impact events 9:30-11:30
//
// Score:  6+ bull signals → bullish, 3-5 → mixed, ≤2 → bearish.
// Confidence: 7-8 aligned = strong, 5-6 = moderate, 3-4 = weak.
//
// Plan derivation:
//   Entry triggers → from price structure (PDH break, pullback to VWAP/gap flip)
//   Stop           → below gap flip (or above, for bearish)
//   Scale ladder   → fractions of distance to call wall (or put wall)
//   Hinges         → borderline signal thresholds become real-time flips
//
// Phase 3 replaces this with a Sonnet call that uses the user
// context blob. For now, deterministic rules get the page to
// day-one working state.
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// SIGNAL SCORING
// ═══════════════════════════════════════════════════════════
//
// CC-6 (2026-05-12): scoreSignals now accepts an optional
// `decayedSignals` array (from method-match.getDecayedSignals).
// For each signal that matches a decayed entry, the bullish tone
// is downgraded to neutral and `decayed:true` flag is attached.
// Without this, "Above PDH" was counted as +1 bullish in the
// 6/10 panel even when method-match flagged it as 75→64% decayed.
//
import { matchDecayForSignalKey } from './method-match.js';

export function scoreSignals(sessionData, decayedSignals = []) {
  const { prices, environment, structure, flow, risk } = sessionData;
  const raw = {
    ...prices._raw,
    ...environment._raw,
    ...structure._raw,
    ...flow._raw,
  };

  const signals = [];

  // 1. Above PDH
  {
    const qqqPrice = num(raw.qqqPrice);
    const pdh = num(raw.qqqS?.prev_high);
    let tone = 'neutral';
    let text = '→ PDH status unclear';
    if (qqqPrice != null && pdh != null) {
      if (qqqPrice > pdh)      { tone = 'bull';    text = '↑ Above PDH'; }
      else if (qqqPrice < num(raw.qqqS?.prev_low)) { tone = 'bear'; text = '↓ Below PDL'; }
      else                     { tone = 'neutral'; text = '→ Inside PDH/PDL'; }
    }
    signals.push({ key: 'pdh', tone, text });
  }

  // 2. Gamma regime
  {
    const above = num(raw.aboveFlip);
    let tone = 'neutral';
    let text = '→ Gamma regime unclear';
    if (above != null) {
      if (above > 0.5)       { tone = 'bull';    text = '↑ Gamma positive'; }
      else if (above < -0.5) { tone = 'bear';    text = '↓ Gamma negative'; }
      else                   { tone = 'neutral'; text = '→ Near gamma flip'; }
    }
    signals.push({ key: 'gamma', tone, text });
  }

  // 3. VIX contango
  {
    const vix9 = num(raw.vix9d);
    const vix  = num(raw.vix);
    let tone = 'neutral';
    let text = '→ VIX term unclear';
    if (vix9 != null && vix != null) {
      if (vix9 < vix - 0.3)      { tone = 'bull';    text = '↑ Contango VIX'; }
      else if (vix9 > vix + 0.3) { tone = 'bear';    text = '↓ Backwardation'; }
      else                       { tone = 'neutral'; text = '→ VIX flat'; }
    }
    signals.push({ key: 'vix_term', tone, text });
  }

  // 4. PCR bullish
  {
    const pcrVal = num(raw.pcrValue);
    let tone = 'neutral';
    let text = '→ PCR awaiting';
    if (pcrVal != null) {
      if (pcrVal < 0.85)      { tone = 'bull';    text = '↑ PCR bullish'; }
      else if (pcrVal > 1.05) { tone = 'bear';    text = '↓ PCR bearish'; }
      else                    { tone = 'neutral'; text = '→ PCR neutral'; }
    }
    signals.push({ key: 'pcr', tone, text });
  }

  // 5. Tech leading
  {
    const delta = num(raw.techLeadingSpy);
    let tone = 'neutral';
    let text = '→ Tech / SPY balanced';
    if (delta != null) {
      if (delta > 0.15)       { tone = 'bull';    text = '↑ Tech leading'; }
      else if (delta < -0.15) { tone = 'bear';    text = '↓ Tech lagging'; }
      else                    { tone = 'neutral'; text = '→ Tech / SPY flat'; }
    }
    signals.push({ key: 'tech', tone, text });
  }

  // 6. Dollar — FRED DTWEXBGS day-over-day. Tech is rate-sensitive,
  // so dollar weakness = bullish for QQQ (inverse correlation).
  {
    const dxyChg = num(raw.dxyChangePct);
    let tone = 'neutral';
    let text = '→ Dollar (mild)';
    if (dxyChg != null) {
      if (dxyChg < -0.15)      { tone = 'bull';    text = '↑ Dollar weak'; }
      else if (dxyChg > 0.15)  { tone = 'bear';    text = '↓ Dollar strong'; }
      else                     { tone = 'neutral'; text = '→ Dollar (mild)'; }
    }
    signals.push({ key: 'dollar', tone, text });
  }

  // 7. Yield curve
  {
    const spread = num(raw.spread10y2y);
    let tone = 'neutral';
    let text = '→ Yield curve';
    if (spread != null) {
      if (spread < -0.25)     { tone = 'bear';    text = '↓ Deeply inverted'; }
      else if (spread < 0)    { tone = 'neutral'; text = '→ Inverted (watch)'; }
      else                    { tone = 'bull';    text = '↑ Curve positive'; }
    }
    signals.push({ key: 'yield', tone, text });
  }

  // 8. Clean window (9:30-11:30)
  {
    const events = Array.isArray(risk) ? risk : [];
    const inWindow = events.filter((e) => e.sev === 'hi');
    let tone = 'bull';
    let text = '↑ Clean window';
    if (inWindow.length > 0) {
      tone = 'bear';
      text = '↓ High-impact event in window';
    } else if (events.some((e) => e.sev === 'md')) {
      tone = 'neutral';
      text = '→ Medium event in window';
    }
    signals.push({ key: 'window', tone, text });
  }

  // 9. Credit spread (HYG vs LQD) — risk-off proxy. Promoted from
  //    enrichment to scored signal in Batch B v2.
  {
    const cs = num(raw.creditSpread);
    let tone = 'neutral';
    let text = '→ Credit flat';
    if (cs != null) {
      if (cs > 0.05)       { tone = 'bull';    text = '↑ Credit risk-on'; }
      else if (cs < -0.05) { tone = 'bear';    text = '↓ Credit stress'; }
      else                 { tone = 'neutral'; text = '→ Credit flat'; }
    }
    signals.push({ key: 'credit', tone, text });
  }

  // 10. Tech breadth (% of QQQ top-10 components positive). The
  //     classic "narrow rally" warning catcher.
  {
    const bp = num(raw.breadthPct);
    let tone = 'neutral';
    let text = '→ Breadth mixed';
    if (bp != null) {
      if (bp >= 70)      { tone = 'bull';    text = '↑ Strong breadth'; }
      else if (bp <= 30) { tone = 'bear';    text = '↓ Narrow breadth'; }
      else               { tone = 'neutral'; text = '→ Breadth mixed'; }
    }
    signals.push({ key: 'breadth', tone, text });
  }

  // CC-6 (2026-05-12): apply decay propagation.
  // If a bullish signal matches a method-sheet decay-watch entry,
  // downgrade its tone (bull → neutral) and tag with decay metadata.
  // Bearish signals are NOT downgraded — bearish-decay would mean
  // "this used to predict downside but now doesn't", which is a
  // DIFFERENT methodology question. Phase 1.5 may add bearish decay.
  if (Array.isArray(decayedSignals) && decayedSignals.length > 0) {
    for (const sig of signals) {
      const decay = matchDecayForSignalKey(sig.key, decayedSignals);
      if (!decay) continue;
      sig.decay = decay;
      // Downgrade only if currently bullish AND decay is meaningful
      // (now_pct < 65 = signal is below "useful edge" threshold)
      if (sig.tone === 'bull' && (decay.now_pct == null || decay.now_pct < 65)) {
        sig.original_tone = sig.tone;
        sig.tone = 'neutral';
        sig.text = `${sig.text} ⚠ decaying (${decay.was_pct ?? '?'}→${decay.now_pct ?? '?'}%)`;
        sig.decayed_downgraded = true;
      }
    }
  }

  const bull = signals.filter((s) => s.tone === 'bull').length;
  const bear = signals.filter((s) => s.tone === 'bear').length;
  const total = signals.length;
  const decayed_count = signals.filter((s) => s.decayed_downgraded).length;

  return { signals, bull, bear, total, decayed_count };
}

// ═══════════════════════════════════════════════════════════
// VERDICT SYNTHESIS
// ═══════════════════════════════════════════════════════════
//
// CC-6 (2026-05-12): buildVerdict now accepts an optional
// `decayedSignals` 2nd arg (from method-match.getDecayedSignals).
// Passed through to scoreSignals so decayed bullish signals
// get downgraded before the verdict is computed. Backwards-
// compatible: omitting the arg preserves v1 behavior.
//
export function buildVerdict(sessionData, decayedSignals = []) {
  const { signals, bull, bear, total, decayed_count } = scoreSignals(sessionData, decayedSignals);

  // Direction thresholds scale with total signals — 60% of total
  // is the bull/bear floor. (Was hardcoded ≥5 of 8; now 60% of 10
  // = 6, but stays correct if total ever changes again.)
  const directionFloor = Math.ceil(total * 0.6);
  let direction, directionTone, scoreN;
  if (bull >= directionFloor && bull > bear) {
    direction = 'Bullish';
    directionTone = 'bull';
    scoreN = bull;
  } else if (bear >= directionFloor && bear > bull) {
    direction = 'Bearish';
    directionTone = 'bear';
    scoreN = bear;
  } else {
    direction = 'Mixed';
    directionTone = 'mixed';
    scoreN = Math.max(bull, bear);
  }

  // Confidence ladder — 80% / 60% / below
  const strongFloor = Math.ceil(total * 0.8);
  const moderateFloor = Math.ceil(total * 0.6);
  let confidence, confidenceTone;
  if (scoreN >= strongFloor)        { confidence = 'Strong confidence';   confidenceTone = 'strong'; }
  else if (scoreN >= moderateFloor) { confidence = 'Moderate confidence'; confidenceTone = 'mod'; }
  else                              { confidence = 'Low confidence';      confidenceTone = 'mod'; }

  // Summary
  const summaryParts = [];
  const raw = sessionData.structure._raw || {};
  if (direction === 'Bullish') {
    summaryParts.push(`${bull} of ${total} signals align bullish.`);
    if (raw.gammaFlip != null && raw.callWall != null && raw.aboveFlip > 0) {
      summaryParts.push(`Price above gamma flip at ${raw.gammaFlip.toFixed(2)}; call wall ${raw.callWall.toFixed(2)} is the session target.`);
    }
    const windowSig = signals.find((s) => s.key === 'window');
    if (windowSig?.tone === 'bear') summaryParts.push('Event in window — reduce hold time or skip.');
  } else if (direction === 'Bearish') {
    summaryParts.push(`${bear} of ${total} signals align bearish.`);
    if (raw.gammaFlip != null && raw.putWall != null) {
      summaryParts.push(`Price below gamma flip; put wall ${raw.putWall.toFixed(2)} is the downside magnet.`);
    }
  } else {
    summaryParts.push(`Split read — ${bull} bull / ${bear} bear / ${total - bull - bear} neutral.`);
    summaryParts.push('Lean smaller or skip — conviction is borderline.');
  }
  const summary = summaryParts.join(' ');

  // Action label + window
  const actionLabel = direction === 'Bullish' ? 'Call setups active ↑'
    : direction === 'Bearish' ? 'Put setups active ↓'
    : 'No edge — skip or size down';
  const actionWindow = 'Window: 9:30 – 11:30 AM ET';

  // Sizing tiers scale with total: full at ≥75%, 75% at ≥50%, else skip
  const fullSizeFloor = Math.ceil(total * 0.75);   // 8 of 10
  const partialFloor  = Math.ceil(total * 0.5);    // 5 of 10
  const sizingActiveIdx = scoreN >= fullSizeFloor ? 1
    : scoreN >= partialFloor ? 2
    : 3;

  // Dynamic sizing-band labels so the card stays correct if total
  // signals ever changes again.
  const sizingOptions = [
    { score: `${fullSizeFloor}–${total} signals`, size: 'Full size' },
    { score: `Today: ${scoreN}`,                  size: scoreN >= fullSizeFloor ? 'Full size'
                                                       : scoreN >= partialFloor  ? '75% size'
                                                       : 'Skip / 50%' },
    { score: `${partialFloor}–${fullSizeFloor - 1} signals`, size: '75% size' },
    { score: `0–${partialFloor - 1} signals`,                size: 'Skip / 50%' },
  ];

  return {
    eyebrow: `QQQ / SPY · First 2 Hours · ${todayLabel()}`,
    direction,
    directionTone,
    scoreN,
    scoreD: total,
    confidence,
    confidenceTone,
    forces: signals.map((s) => ({
      text: s.text,
      tone: s.tone,
      // CC-6: surface decay metadata so the UI can show ⚠
      decayed: !!s.decayed_downgraded,
      decay_info: s.decay || null,
    })),
    summary,
    window: actionWindow,
    actionLabel,
    sizingActiveIdx,
    sizingOptions,
    // CC-6: top-level decay count for orchestrator/UI summary
    decayed_count: decayed_count || 0,
  };
}

// ═══════════════════════════════════════════════════════════
// EXECUTION PLAN SYNTHESIS
// ═══════════════════════════════════════════════════════════
export function buildExecutionPlan(sessionData, verdict) {
  const raw = {
    ...sessionData.prices._raw,
    ...sessionData.structure._raw,
    ...sessionData.environment._raw,
  };

  const qqqPrice = num(raw.qqqPrice);
  const pdh = num(raw.qqqS?.prev_high);
  const pdl = num(raw.qqqS?.prev_low);
  const gapFlip = num(raw.gapFlip);
  const gammaFlip = num(raw.gammaFlip);
  const callWall = num(raw.callWall);
  const putWall = num(raw.putWall);
  const vix = num(raw.vix);

  const isBull = verdict.directionTone === 'bull';
  const isBear = verdict.directionTone === 'bear';

  // Stance
  let stance, stanceTone;
  if (isBull) {
    stance = 'Call setups only — long bias from aligned structure';
    stanceTone = 'bull';
  } else if (isBear) {
    stance = 'Put setups only — short bias from negative structure';
    stanceTone = 'bear';
  } else {
    stance = 'Mixed signals — reduced size or skip the session';
    stanceTone = 'neutral';
  }

  // Entry triggers
  const entryTriggers = [];
  if (isBull && pdh != null) {
    entryTriggers.push({
      priority: 'primary',
      condition: `Break above PDH ${pdh.toFixed(2)}`,
      filter: 'Volume > 15k contracts first 5 min',
    });
    if (gapFlip != null && qqqPrice != null && qqqPrice > gapFlip) {
      entryTriggers.push({
        priority: 'secondary',
        condition: `Pullback to ${gapFlip.toFixed(2)} (gap flip)`,
        filter: 'VWAP reclaim + 5-min hold',
      });
    }
  } else if (isBear && pdl != null) {
    entryTriggers.push({
      priority: 'primary',
      condition: `Break below PDL ${pdl.toFixed(2)}`,
      filter: 'Volume > 15k contracts · no dip-buying',
    });
    if (gammaFlip != null) {
      entryTriggers.push({
        priority: 'secondary',
        condition: `Rejection at gamma flip ${gammaFlip.toFixed(2)}`,
        filter: 'Failed reclaim + volume',
      });
    }
  } else {
    entryTriggers.push({
      priority: 'primary',
      condition: 'Wait for directional resolution',
      filter: 'No entries in chop regime',
    });
  }

  // Stop
  let stop = null;
  if (isBull) {
    const stopPrice = gapFlip != null ? gapFlip - 0.25
      : (putWall != null ? putWall : (qqqPrice != null ? qqqPrice * 0.995 : null));
    if (stopPrice != null) {
      stop = {
        price: stopPrice.toFixed(2),
        riskR: '0.3R',
        accountRiskPct: '0.5%',
        rationale: gapFlip != null
          ? `Below gap flip ${gapFlip.toFixed(2)} breaks the structural setup`
          : 'Structural level invalidation — not arbitrary distance',
      };
    }
  } else if (isBear) {
    const stopPrice = gammaFlip != null ? gammaFlip + 0.25
      : (qqqPrice != null ? qqqPrice * 1.005 : null);
    if (stopPrice != null) {
      stop = {
        price: stopPrice.toFixed(2),
        riskR: '0.3R',
        accountRiskPct: '0.5%',
        rationale: gammaFlip != null
          ? `Above gamma flip ${gammaFlip.toFixed(2)} breaks the bearish setup`
          : 'Structural level invalidation',
      };
    }
  }

  // Scale ladder — divide distance to target wall into 3 tranches
  let scaleLadder = [];
  if (isBull && qqqPrice != null && callWall != null && callWall > qqqPrice) {
    const distance = callWall - qqqPrice;
    const t1 = qqqPrice + distance * 0.45;
    const t2 = qqqPrice + distance * 0.75;
    const t3 = callWall;
    scaleLadder = [
      { tranche: 'T1', share: '50%', price: t1.toFixed(2),  rMultiple: '+0.7R', note: null },
      { tranche: 'T2', share: '30%', price: t2.toFixed(2),  rMultiple: '+1.1R', note: 'move stop to BE after T1' },
      { tranche: 'T3', share: '20%', price: `${t3.toFixed(2)}+`, rMultiple: 'runner', note: 'trail behind prior target' },
    ];
  } else if (isBear && qqqPrice != null && putWall != null && putWall < qqqPrice) {
    const distance = qqqPrice - putWall;
    const t1 = qqqPrice - distance * 0.45;
    const t2 = qqqPrice - distance * 0.75;
    const t3 = putWall;
    scaleLadder = [
      { tranche: 'T1', share: '50%', price: t1.toFixed(2),  rMultiple: '+0.7R', note: null },
      { tranche: 'T2', share: '30%', price: t2.toFixed(2),  rMultiple: '+1.1R', note: 'move stop to BE after T1' },
      { tranche: 'T3', share: '20%', price: `${t3.toFixed(2)}−`, rMultiple: 'runner', note: 'trail behind prior target' },
    ];
  }

  // Hinges — real-time falsification conditions
  const hinges = [];
  if (isBull) {
    if (gammaFlip != null) {
      hinges.push({
        condition: `QQQ below ${gammaFlip.toFixed(2)} (gamma flip)`,
        impact: 'regime flip — close longs, consider reverse',
        severity: 'hi',
      });
    }
    if (vix != null) {
      hinges.push({
        condition: `VIX spikes above ${(vix + 1.5).toFixed(1)}`,
        impact: 'vol breakout — reduce size 50%',
        severity: 'md',
      });
    }
    hinges.push({
      condition: 'DXY breaks +0.5% intraday',
      impact: 'risk-off overlay — take partials',
      severity: 'md',
    });
    const windowSig = verdict.forces.find((f) => f.text.includes('event'));
    if (windowSig) {
      hinges.push({
        condition: 'High-impact news released inside window',
        impact: 'news override — flatten, wait for 11:30',
        severity: 'hi',
      });
    }
  } else if (isBear) {
    if (gammaFlip != null) {
      hinges.push({
        condition: `QQQ above ${gammaFlip.toFixed(2)} (gamma flip reclaim)`,
        impact: 'regime flip — close shorts',
        severity: 'hi',
      });
    }
    hinges.push({
      condition: 'VIX drops below entry day’s low',
      impact: 'vol compression — bearish setup weakens',
      severity: 'md',
    });
    hinges.push({
      condition: 'Broad market (SPY) reclaims above open',
      impact: 'risk-on shift — close shorts',
      severity: 'md',
    });
  } else {
    hinges.push({
      condition: 'Any 2 signals flip to aligned direction',
      impact: 'wait for cleaner setup',
      severity: 'md',
    });
  }

  return {
    stance,
    stanceTone,
    entryTriggers,
    stop,
    scaleLadder,
    hinges,
    rules: {
      maxEntries: '2',
      maxEntriesNote: 'Skip if first stops out AND a hinge condition fires',
      maxHold: '11:30 AM ET',
      maxHoldNote: 'Window closes — exit all regardless of P&L',
      reentry: 'Only on second trigger re-forming. No chasing a failed break.',
    },
  };
}

// ─── helpers ───
function num(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function todayLabel() {
  try {
    return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' });
  } catch { return ''; }
}
